import { getGridStringOperators, type GridFilterOperator } from '@mui/x-data-grid-premium';

export const convertValueToStringArray = (value: unknown): string[] => {
    if (Array.isArray(value)) return value as string[];
    if (typeof value === 'string' && value) {
        return value
            .split(',')
            .map((s) => s.trim())
            .filter((s) => s);
    }
    if (value) return [String(value)];
    return [];
};

export const convertValueToLabels = (value: unknown): string[] => {
    if (Array.isArray(value)) {
        return value
            .map((item: any) => {
                if (typeof item === 'string') {
                    return item;
                }
                if (item && typeof item === 'object') {
                    return item.label ?? item.reference?.label ?? item.id ?? item.reference?.id ?? String(item);
                }
                return String(item);
            })
            .filter(Boolean);
    }

    if (typeof value === 'string') {
        return convertValueToStringArray(value);
    }

    if (value && typeof value === 'object') {
        const obj = value as any;
        return [obj.label ?? obj.reference?.label ?? obj.id ?? obj.reference?.id ?? String(value)];
    }

    return [];
};

/**
 * Extracts a single sortable/filterable string from a multi-select cell value.
 * For reference-type columns the labels are joined into a comma-separated string.
 * For plain string arrays the values are joined as-is.
 */
export const extractMultiSelectSortValue = (value: unknown, isReferenceType: boolean): string => {
    const labels = isReferenceType ? convertValueToLabels(value) : convertValueToStringArray(value);
    return labels.join(', ').toLowerCase();
};

/**
 * Safely converts a value to an array.
 * - If value is undefined, returns empty array
 * - If value is already an array, returns it as-is
 * - If value is truthy, wraps it in an array
 * - Otherwise returns empty array
 */
export function makeSafeArrayValue<T>(value: T | T[] | undefined): T[] {
    if (value === undefined) {
        return [];
    }
    if (Array.isArray(value)) {
        return value;
    }
    return value ? [value] : [];
}

/**
 * Extracts a stable identity key from a value.
 * For reference objects with an `id` property, returns the `id`.
 * For primitive values, returns the value itself converted to a string.
 */
export function extractIdentityKey(value: unknown): string {
    if (value !== null && typeof value === 'object' && 'id' in (value as Record<string, unknown>)) {
        return String((value as Record<string, unknown>).id);
    }

    return String(value);
}

/**
 * Compares two multi-select cell values for sorting.
 * Extracts label strings and performs locale-aware comparison.
 */
export function createMultiSelectSortComparator(isReferenceType: boolean) {
    return (v1: unknown, v2: unknown): number => {
        const a = extractMultiSelectSortValue(v1, isReferenceType);
        const b = extractMultiSelectSortValue(v2, isReferenceType);
        return a.localeCompare(b);
    };
}

/** Filter operators for multi-select columns; matches against extracted labels. */
export function createMultiSelectFilterOperators(isReferenceType: boolean): GridFilterOperator[] {
    const toLabels = (value: unknown) =>
        (isReferenceType ? convertValueToLabels(value) : convertValueToStringArray(value)).map((label) =>
            label.toLowerCase()
        );

    const withQuery =
        (match: (labels: string[], query: string) => boolean): GridFilterOperator['getApplyFilterFn'] =>
        (item) => {
            if (!item.value) return null;
            const query = String(item.value).toLowerCase().trim();
            return (value) => match(toLabels(value), query);
        };

    const applyFns: Record<string, GridFilterOperator['getApplyFilterFn']> = {
        contains: withQuery((labels, query) => labels.some((label) => label.includes(query))),
        doesNotContain: withQuery((labels, query) => !labels.some((label) => label.includes(query))),
        equals: withQuery((labels, query) => labels.some((label) => label === query)),
        doesNotEqual: withQuery((labels, query) => !labels.some((label) => label === query)),
        startsWith: withQuery((labels, query) => labels.some((label) => label.startsWith(query))),
        endsWith: withQuery((labels, query) => labels.some((label) => label.endsWith(query))),
        isEmpty: () => (value) => toLabels(value).length === 0,
        isNotEmpty: () => (value) => toLabels(value).length > 0,
        isAnyOf: (item) => {
            if (!Array.isArray(item.value) || item.value.length === 0) return null;
            const queries = new Set(item.value.map((entry) => String(entry).toLowerCase().trim()));
            return (value) => toLabels(value).some((label) => queries.has(label));
        }
    };

    return getGridStringOperators().flatMap((operator) => {
        const getApplyFilterFn = applyFns[operator.value];
        return getApplyFilterFn ? [{ ...operator, getApplyFilterFn }] : [];
    });
}

export function getLabel(val: unknown): string {
    if (typeof val === 'string') {
        return val;
    }

    if (val && typeof val === 'object') {
        const obj = val as Record<string, unknown>;
        return String(obj.label ?? (obj.reference as Record<string, unknown>)?.label ?? obj.id ?? val);
    }

    return String(val);
}
